import { Link } from 'react-router-dom'
import { useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { usagefees } from '../../api/fee'
import type { UsageFee } from '../../api/types'
import { PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { usageFeePriceText } from '../../lib/catalogue'
import { useEmailParam } from '../../lib/useEmailParam'
import { AdminUserLocalMenu } from './AdminUserLocalMenu'

/**
 * Port of `AdminUserUsagefeeListComponent` (`…/admin/users/usagefees?email=`).
 * The legacy page edited per-customer custom usage-fee prices through
 * `/admin/customusagefees*` — routes the legacy backend never registered
 * (port plan §1.3, "the whole admin custom usage fee page"), so that editor
 * could never have saved anything and is not carried over. What remains is
 * the read-only fee list the page showed (every customer pays the fee's own
 * price, which is what the legacy backend actually billed).
 */
export function AdminUserUsagefeePage() {
  const { projectId, project } = useProject()
  const email = useEmailParam()
  const q = useApiQuery(() => usagefees.search(projectId), [projectId])
  const columns: Column<UsageFee>[] = [
    {
      key: 'name',
      label: 'Usage Fee',
      format: (_v, f) => <Link to={`/projects/${encodeURIComponent(projectId)}/usagefees/${encodeURIComponent(f.id)}`}>{f.name}</Link>,
    },
    { key: 'price', label: 'Original Price/unit', align: 'right', numeric: true, format: (_v, f) => usageFeePriceText(f) },
    { key: 'custom', label: 'Custom Price/unit', sortable: false, format: () => '(x100%)' },
  ]
  return (
    <div className="portal-card">
      <PageTitle title={`Usage fees — ${project.name}`}>
        <AdminUserLocalMenu email={email} title="Usage fees" />
      </PageTitle>
      <p className="portal-notice portal-notice--info">
        Per-customer custom usage-fee prices cannot be edited: the backend has no route for them (it never had one).
      </p>
      <QueryState loading={q.loading} error={q.error}>
        <div className="portal-table-scroll">
          <Table columns={columns} rows={q.data ?? []} rowKey={(f) => f.id} />
        </div>
      </QueryState>
    </div>
  )
}
